import { TrendingUp, TrendingDown, FileCheck, Sparkles, HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { ImportSummary as ImportSummaryType } from '@/hooks/use-import-statement';

interface ImportSummaryProps {
  summary: ImportSummaryType;
  fileName?: string;
  bankName?: string;
}

export function ImportSummary({ summary, fileName, bankName }: ImportSummaryProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="space-y-4">
      {(fileName || bankName) && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FileCheck className="w-4 h-4" />
          <span>{bankName || 'Extrato'}</span>
          {fileName && <span className="text-xs">({fileName})</span>}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 bg-muted/30">
          <p className="text-xs text-muted-foreground mb-1">Transações</p>
          <p className="text-2xl font-bold">{summary.total}</p>
        </Card>

        <Card className="p-4 bg-success/10 border-success/20">
          <div className="flex items-center gap-1 text-xs text-success mb-1">
            <TrendingUp className="w-3 h-3" />
            Receitas
          </div>
          <p className="text-lg font-bold text-success">{formatCurrency(summary.income)}</p>
        </Card>

        <Card className="p-4 bg-destructive/10 border-destructive/20">
          <div className="flex items-center gap-1 text-xs text-destructive mb-1">
            <TrendingDown className="w-3 h-3" />
            Despesas
          </div>
          <p className="text-lg font-bold text-destructive">{formatCurrency(summary.expense)}</p>
        </Card>

        <Card className="p-4 bg-primary/10 border-primary/20">
          <p className="text-xs text-muted-foreground mb-1">Saldo Líquido</p>
          <p className={`text-lg font-bold ${summary.income - summary.expense >= 0 ? 'text-success' : 'text-destructive'}`}>
            {formatCurrency(summary.income - summary.expense)}
          </p>
        </Card>
      </div>

      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success" />
          <span className="text-muted-foreground">
            {summary.auto_categorized} auto-categorizadas
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-warning" />
          <span className="text-muted-foreground">
            {summary.suggested} sugeridas
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-muted-foreground" />
          <span className="text-muted-foreground">
            {summary.uncategorized} sem categoria
          </span>
        </div>
      </div>
    </div>
  );
}
